import { FormikProps } from "formik";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { IneligibleSettingsContext } from "../../../../context/ineligibleSettingsContext";
import { IFormikValuesForRuleAndOverridesPerSetting, IFormikValuesForUPCRuleAndOverridesPerSetting, IIneligibleRuleConditionGroup, IIneligibleRuleOverride, IIneligibleSettingsContext } from "../../../../interfaces/ineligibleSettingInterface";
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Checkbox, CircularProgress, Divider, Grid, IconButton, InputAdornment, Link, MenuItem, Modal, Paper, Select, SxProps, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Theme, Tooltip, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import styles from "./styles";
import { formatDate, getApplicableCustomersRequest, getApplicableCustomersUpcRequest, getApplicableItemsRequest } from "../../../../utility/helper";
import { AMERICAN_DATE_FORMAT, PROMPT } from "../../../../utility/constants";
import { IARCollateral, IARCustomer, IBBPeriod, IClient, ICollateral, IInvItem } from "../../../../interfaces";
import SearchIcon from '@mui/icons-material/Search';
import NoDataPlaceholder from "../../../common/no-data-placeholder";
import { ExpandMore } from "@mui/icons-material"
import ConfirmModal from "../../../modals/confirm-modal";
import { SelectedClientContext } from "../../../../context/selectedClientContext";
import OverflowText from "../../../common/overflow-text";
import { getAsOfDates, getMostRecentlyUpdatedAsOfDate, getMostRecentlyUpdatedUPCAsOfDate, getParentCustomersByARCollateralId, getUPCAsOfDates, getUPCParentCustomersByBorrowerId } from "../../../../api/ineligible-settings";

export interface IViewApplicableItemsProps {
  formik                : FormikProps<IFormikValuesForRuleAndOverridesPerSetting> | FormikProps<IFormikValuesForUPCRuleAndOverridesPerSetting>;
  idx                   : number;
  collateralType?       : string;
}

/**
 * Component displays a list of applicable customers for an ineligible rule override.
 * It allows the user to include or exclude customers and provides a search and filter functionality.
 * @param props The component's props.
 * @returns {JSX.Element} The rendered View Applicable Customers button and its Customer List View modal component.
 */
const ViewApplicableItems: FC<IViewApplicableItemsProps> = (props) => {
  const { selectedClient } = useContext(SelectedClientContext);
  const {
    selectedIneligibleIndex: ineligibleIndex,
    asOfDates, setAsOfDates,
    mostRecentlyUpdatedAsOfDate, setMostRecentlyUpdatedAsOfDate,
    selectedCollateral,
    selectedAsOfDate, setSelectedAsOfDate
    // parentCustomers, setParentCustomers
  } = useContext(IneligibleSettingsContext) as IIneligibleSettingsContext;

  const isSelectedClientUPC = () => (selectedClient as IClient).parentClient;

  const selectedIneligibleRuleOverride = useMemo(() => (
    (props.formik.values.ineligibleSettingDetails[ineligibleIndex]?.ineligibleRuleOverrides as IIneligibleRuleOverride[])[props.idx]
  ), [ineligibleIndex, props.formik.values.ineligibleSettingDetails, props.idx]);

  const [applicableItems, setApplicableItems] = useState<IInvItem[]>([]);
  const [excludedItemIds, setExcludedItemIds] = useState(selectedIneligibleRuleOverride.excludedItems || []);
  const [isLoading, setIsLoading] = useState(false);
  const [isItemListViewModalOpen, setIsItemListViewModalOpen] = useState(false);
  const [isConfirmationModalForUnsavedChangesOpen, setIsConfirmationModalForUnsavedChangesOpen] = useState(false);
  const [isViewByItem, setIsViewByItem] = useState(true);
  const [selectedFilterType, setSelectedFilterType] = useState<'All' | 'Parent' | 'Child'>('All');
  const [filterText, setFilterText] = useState('');
  const [scrolledRowIndex, setScrolledRowIndex] = useState(0);
  const NUMBER_OF_ROWS_PER_SCROLL = 15;

  const getFormikFieldNameForExcludedItemsOnSelectedOverride = () => `ineligibleSettingDetails[${ineligibleIndex}].ineligibleRuleOverrides[${props.idx}].excludedItems`;

  useEffect(() => {
    if (!isItemListViewModalOpen) { return; }
    setApplicableItems([]); /* reset last sessions' applicable items */
    async function fetchSelectedAsOfDateAndCustomers() {
      const [ asOfDates ] = await Promise.all([ loadAsOfDates() ]);
      // , loadParentCustomers()
      const mostRecentlyUpdatedAsOfDate = await loadMostRecentlyUpdatedAsOfDate(asOfDates);
      setSelectedAsOfDate(mostRecentlyUpdatedAsOfDate);
      await getApplicableItems(mostRecentlyUpdatedAsOfDate);
    }
    fetchSelectedAsOfDateAndCustomers();
  }, [isItemListViewModalOpen]);

  const selectedIneligibleDescription = useMemo(() => (
    props.formik.values.ineligibleSettingDetails[ineligibleIndex].ineligibleSetting.description
  ), [ineligibleIndex, props.formik.values.ineligibleSettingDetails]);

  const filteredApplicableItems = useMemo(() => {
    if (filterText === '') { return applicableItems; }
    return applicableItems.filter(item => {
      // if (selectedFilterType === 'Child' && isSelectedClientUPC() && customer.upcParentCustId === undefined) { return false; }
      // if (selectedFilterType === 'Child' && !isSelectedClientUPC() && customer.parentARCustomerId === undefined) { return false; }
      const customerAttributeValuesToMatch = selectedFilterType === 'All' ? [ item.itemName, item.skuItemNumber ] : [];
      // || selectedFilterType === 'Child'
      // const collateralLevelParentCustomerAttributes = [ customer.parentCustName, customer.parentCustSrcId ];
      // const upcLevelParentCustomerAttributes = [ customer.upcParentCustName, customer.upcParentCustSrcId ];
      // const parentCustomerAttributes = isSelectedClientUPC() ? upcLevelParentCustomerAttributes : collateralLevelParentCustomerAttributes ;
      // const parentCustomerAttributeValuesToMatch = (selectedFilterType === 'All' || selectedFilterType === 'Parent') ? parentCustomerAttributes : [];
      return [ ...customerAttributeValuesToMatch ].some(text => text?.toLowerCase()?.includes(filterText.toLowerCase()));
      // , ...parentCustomerAttributeValuesToMatch
    });
  }, [applicableItems, filterText, selectedFilterType]);

  const hasMoreRowsToRender = useMemo(() => {
    const lengthOfRenderedRows = filteredApplicableItems.slice(0, scrolledRowIndex * NUMBER_OF_ROWS_PER_SCROLL + NUMBER_OF_ROWS_PER_SCROLL).length;
    return applicableItems.length > lengthOfRenderedRows;
  }, [applicableItems, filteredApplicableItems, scrolledRowIndex]);

  const observer = useRef<any>();
  const rowElementRef = useCallback((node: any) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => (entries[0].isIntersecting && hasMoreRowsToRender) && setScrolledRowIndex((prevValue) => prevValue + 1))
    if (node) observer.current.observe(node);
  }, [isLoading, hasMoreRowsToRender]);

  const openCustomerListViewModal = async () => {
    setIsViewByItem(true);
    setSelectedFilterType('All');
    setExcludedItemIds(selectedIneligibleRuleOverride.excludedItems || []);
    setIsItemListViewModalOpen(true);
  };

  const getApplicableItems = async(asOfDate: IBBPeriod | null) => {
    if (asOfDate === null) { setApplicableItems([]); return; }
    try {
      setIsLoading(true);
      // const nonNullSelectedClientRecordId = selectedClient?.recordId as NonNullable<number>;
      const nonNullSelectedCollateral = selectedCollateral as NonNullable<ICollateral>;
      // const endDate = formatDate(asOfDate.endDate, 'YYYY-MM-DD');
      const payload = selectedIneligibleRuleOverride.ineligibleRuleConditionGroup.map((condition: IIneligibleRuleConditionGroup) => {
        return {
          irConditions: condition.ineligibleRuleCondition,
          irConditionGroupId: condition.irConditionGroupId,
          irOverrideId: condition.irOverrideId,
          logicalOperator: condition.logicalOperator,
          order: condition.order
        }
      });
      const applicableItems = (await getApplicableItemsRequest(nonNullSelectedCollateral.recordId, asOfDate.recordId, payload)).data as IInvItem[];
      // const parentCustomerIds = applicableItems.map(customer => isSelectedClientUPC() ? customer.upcParentCustId : customer.parentARCustomerId).filter(customerId => customerId !== undefined);
      setApplicableItems(applicableItems);
    } catch (error) {
      console.log('APPLICABLE ITEMS API', error);
      setApplicableItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAsOfDates = async () => {
    if (asOfDates !== 'UNLOADED') { return asOfDates; }
    const loadedAsOfDates = isSelectedClientUPC()
      ? await getUPCAsOfDates(selectedClient as Required<IClient>)
      : await getAsOfDates(selectedClient as Required<IClient>, selectedCollateral as ICollateral);
    setAsOfDates(loadedAsOfDates);
    return loadedAsOfDates;
  };

  const loadMostRecentlyUpdatedAsOfDate = async (asOfDates: IBBPeriod[]) => {
    if (mostRecentlyUpdatedAsOfDate !== 'UNLOADED') { return mostRecentlyUpdatedAsOfDate; }
    const loadedMostRecentlyUpdatedAsOfDate = isSelectedClientUPC()
      ? await getMostRecentlyUpdatedUPCAsOfDate(selectedClient as Required<IClient>, asOfDates)
      : await getMostRecentlyUpdatedAsOfDate(selectedCollateral as ICollateral, asOfDates);
    setMostRecentlyUpdatedAsOfDate(loadedMostRecentlyUpdatedAsOfDate);
    return loadedMostRecentlyUpdatedAsOfDate;
  };

  const handleSelectItemToExclude = (isChecked: boolean, itemId: number) => {
    (!isChecked && !excludedItemIds.includes(itemId)) && setExcludedItemIds([ ...excludedItemIds, itemId ]);
    (isChecked && excludedItemIds.includes(itemId)) && setExcludedItemIds( [ ...excludedItemIds.filter(excludedItemId => excludedItemId !== itemId) ]);
  };

  const handleSaveChanges = () => {
    props.formik.setFieldValue(getFormikFieldNameForExcludedItemsOnSelectedOverride(), excludedItemIds);
    setIsItemListViewModalOpen(false);
  };

  const hasUnsavedChangesOnExcludedItems = () => {
    if (excludedItemIds === undefined || selectedIneligibleRuleOverride.excludedItems === undefined) {
      return false
    }; 

    const excludedItemIdsToSort = [ ...excludedItemIds ];
    const excludedItemIdsOnFormikToSort = [ ...selectedIneligibleRuleOverride.excludedItems ];
    excludedItemIdsToSort.sort((a, b) => (a - b));
    excludedItemIdsOnFormikToSort.sort((a, b) => (a - b));
    return JSON.stringify(excludedItemIdsToSort) !== JSON.stringify(excludedItemIdsOnFormikToSort);
  };

  const isModalLoading = () => isLoading || asOfDates === 'UNLOADED';

  const renderViewApplicableItemsLink = () => {
    const field = selectedIneligibleRuleOverride.ineligibleRuleConditionGroup[0].ineligibleRuleCondition[0].field ?? '';
    const operation = selectedIneligibleRuleOverride.ineligibleRuleConditionGroup[0].ineligibleRuleCondition[0].operation ?? '';
    const values = selectedIneligibleRuleOverride.ineligibleRuleConditionGroup[0].ineligibleRuleCondition[0].fieldValues[0] ?? '';
    const isDisabled = [field, operation, values].some(text => text === '');
    if (isDisabled) {
      return (
        <Tooltip title='Please Set an Override Criteria' placement='bottom-start'>
          <Link
            component='button' type='button'
            variant='body2' underline='none'
            sx={styles.disabledLinkForViewApplicableCustomers}
            disabled>
              View Applicable Items
          </Link>
        </Tooltip>
      );
    }
    return (
      <Link
        component='button' type='button'
        variant='body2' underline='hover'
        sx={styles.linkForViewApplicableCustomers}
        onClick={openCustomerListViewModal}>
          View Applicable Items
      </Link>
    );
  };

  const renderItemListViewModal = () => {
    const handleClose = () => {
      if (hasUnsavedChangesOnExcludedItems()) {
        setIsConfirmationModalForUnsavedChangesOpen(true);
        return;
      }
      setIsItemListViewModalOpen(false);
    };

    return (
      <Modal
        open={isItemListViewModalOpen}
        onClose={handleClose}>
        <Grid container sx={styles.modalContainer}>
          <Grid item xs={12} sx={styles.gridContainerForHeadingAndCloseButton}>
            { renderGridForHeadingAndCloseButton()}
          </Grid>
          <Grid item xs={12}>
            <Divider orientation='vertical' flexItem sx={styles.divider} />
          </Grid>
          <Grid item xs={12} sx={styles.gridContainerForFilterFields}>
            <Box sx={styles.viewByLabel}></Box>
            { renderGridForFilterFields() }
          </Grid>
          <Grid item xs={12} sx={styles.gridContainerForMainContent}>
            { renderGridForMainContent() }
          </Grid>
          <Grid item xs={12} sx={styles.gridContainerForCancelAndSaveButtons}>
            { renderGridForCancelAndSaveButtons() }
          </Grid>
        </Grid>
      </Modal>
    );
  };

  const renderConfirmationModalForUnsavedChanges = () => (
    <ConfirmModal
      open={isConfirmationModalForUnsavedChangesOpen}
      onClose={() => { setIsConfirmationModalForUnsavedChangesOpen(false); setIsItemListViewModalOpen(false); }}
      onConfirm={() => setIsConfirmationModalForUnsavedChangesOpen(false)}
      onButtonClose={() => setIsConfirmationModalForUnsavedChangesOpen(false)}
      promptChecker
      title={PROMPT.NAV_PROMPT.title}
      description='You have unsaved changes. Are you sure you want to leave this modal?'
      yesButtonText={PROMPT.NAV_PROMPT.keepEditing}
      noButtonText={PROMPT.NAV_PROMPT.discardChanges}
      confirmOnly
    />
  );

  const renderGridForHeadingAndCloseButton = () => (
    <Grid container justifyContent='space-between' alignItems='center'>
      <Typography variant='h6' component='h2' sx={styles.modalHeading}>
        Item List View: { selectedIneligibleDescription }
      </Typography>
      <IconButton aria-label='Close icon' onClick={() => setIsItemListViewModalOpen(false)} sx={styles.closeIconButton}>
        <CloseIcon fontSize='inherit' />
      </IconButton>
    </Grid>
  );

  const renderGridForFilterFields = () => (
    <Box sx={styles.containerForFilterFields}>
      <Autocomplete
        value={selectedAsOfDate}
        options={asOfDates !== 'UNLOADED' ? asOfDates : []}
        onChange={(_event, asOfDate: IBBPeriod | null) => { setSelectedAsOfDate(asOfDate); getApplicableItems(asOfDate); }}
        getOptionLabel={(asOfDates) => formatDate(asOfDates.endDate, AMERICAN_DATE_FORMAT)}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={'Select As of Date'}
            inputProps={{ ...params.inputProps, 'aria-label': 'As of Date' }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  { asOfDates === 'UNLOADED' ? <CircularProgress color='inherit' size={15} /> : null }
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        blurOnSelect
        disablePortal
        fullWidth
        size='small'
        componentsProps={{
          popupIndicator: { 'aria-label':'Dropdown icon', tabIndex: 0 },
          clearIndicator: { 'aria-label':'Clear icon', tabIndex: 0}
        }}
        sx={styles.inputForAsOfDate}
        disabled={isModalLoading()}
        loading={asOfDates === 'UNLOADED'}
      />
      <Box sx={styles.containerForSearchFields}>
        <Select
          defaultValue='All'
          onChange={e => setSelectedFilterType(e.target.value as 'All' | 'Parent' | 'Child')}
          sx={styles.inputForSearchBasedOnType}
          inputProps={{ 'aria-label': 'Filter Dropdown', 'aria-labelledby': 'filter-dropdown' }}
          disabled={true}
        >
          <MenuItem value='All' sx={styles.menuItemForSearchBasedOnType}> All </MenuItem>
        </Select>
        <TextField
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          inputProps={{ 'aria-label': 'Search-Textfield' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start' tabIndex={0} aria-label='Search icon'>
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          placeholder='Search'
          size='small'
          sx={styles.inputForSearch}
          disabled={isModalLoading()}
        />
      </Box>
    </Box>
  );

  const renderGridForMainContent = () => {
    if (isModalLoading()) { return <Box sx={styles.loadingContainer}><CircularProgress size={30} /></Box> }
    if (filteredApplicableItems.length === 0) { return <NoDataPlaceholder messageText='No Applicable Items' messageContainerStyle={styles.noDataPlaceholderContainer} />; }
    return renderTableForViewByItem();
  };

  const renderTableForViewByItem = () => {
    const tableHeadCells = [
      { label: 'Item Name', style: { ...styles.viewByCustomerTableHeadCell } },
      { label: 'SKU/Item Number', style: { ...styles.viewByCustomerTableHeadCell } },
      { label: 'Selected', style: { ...styles.viewByCustomerTableHeadCell, textAlign: 'center' } }
    ];

    const renderTableCell = (text: string | number | undefined, textStyle: SxProps<Theme>) => {
      const renderedText = typeof text === 'number' ? text.toString() : text ?? '';
      return (
        <TableCell align='left' sx={styles.viewByCustomerTableCell}>
          <Typography tabIndex={0} sx={textStyle}>
            <OverflowText>
              { renderedText }
            </OverflowText>
          </Typography>
        </TableCell>
      );
    };

    return (
      <TableContainer sx={styles.viewByCustomerTableContainer}>
        <Table aria-label='Table for View By Item'>
          <TableHead sx={styles.viewByCustomerTableHead}>
            <TableRow>
              { tableHeadCells.map(tableHeadCell => (<TableCell tabIndex={0} key={tableHeadCell.label} sx={tableHeadCell.style}> { tableHeadCell.label } </TableCell>)) }
            </TableRow>
          </TableHead>
          <TableBody>
          {
            filteredApplicableItems.slice(0, scrolledRowIndex * NUMBER_OF_ROWS_PER_SCROLL + NUMBER_OF_ROWS_PER_SCROLL).map(item => (
              <TableRow ref={rowElementRef} sx={styles.viewByCustomerTableRow} key={item.recordId}>
                { renderTableCell(item.itemName, styles.viewByCustomerTableCellText) }
                { renderTableCell(item.skuItemNumber, styles.viewByCustomerTableCellText) }
                <TableCell sx={styles.viewByCustomerTableCellForCheckbox}>
                  <Checkbox
                    checked={!excludedItemIds.includes(item.recordId as number)}
                    onChange={event => handleSelectItemToExclude(event.target.checked, item.recordId as number)}
                    inputProps={{ 'aria-label': 'Include Item?' }} />
                </TableCell>
              </TableRow>
            ))
          }
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderGridForCancelAndSaveButtons = () => {
    return (
      <Grid container justifyContent='flex-end'>
        <Button
          variant='outlined' size='small'
          onClick={() => setIsItemListViewModalOpen(false)}
          sx={styles.cancelButton}>
          Cancel
        </Button>
        <Button
          variant='contained' size='small' sx={styles.saveButton}
          onClick={handleSaveChanges}
          disabled={!hasUnsavedChangesOnExcludedItems()}
          >
          Save Changes
        </Button>
      </Grid>
    );
  };

  return (
    <>
      { renderViewApplicableItemsLink() }
      { renderItemListViewModal() }
      { renderConfirmationModalForUnsavedChanges() }
    </>
  );
};

export default ViewApplicableItems;
